import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Button, Card, Input, Select, Spinner, Badge } from "./ui";

type EcoLabel = "ecologic" | "economic" | "illogic";
type RuleLevel = "article" | "brand" | "category";
type RuleOp = "contains" | "equals" | "starts" | "anyOf";
/** Must match ANY_OF_SEP on the server. */
const ANY_OF_SEP = "|";

const LABELS: { id: EcoLabel; label: string; color: "green" | "amber" | "gray" }[] = [
  { id: "ecologic", label: "Ecologic", color: "green" },
  { id: "economic", label: "Economic", color: "amber" },
  { id: "illogic", label: "Illogic", color: "gray" },
];
const LEVELS: { id: RuleLevel; label: string; hint: string }[] = [
  { id: "article", label: "Artikel", hint: "kijkt naar de PBS-attributen van het artikel zelf (FSC, EU Ecolabel, …)" },
  { id: "brand", label: "Merk", hint: "geldt voor alle artikelen van dit merk" },
  { id: "category", label: "Categorie", hint: "geldt voor de categorie én alles eronder" },
];
const OPS: { id: RuleOp; label: string }[] = [
  { id: "contains", label: "bevat" },
  { id: "equals", label: "is exact" },
  { id: "starts", label: "begint met" },
  { id: "anyOf", label: "is één van" },
];

const emptyDraft = {
  id: undefined as number | undefined,
  label: "ecologic" as EcoLabel,
  level: "article" as RuleLevel,
  field: "",
  op: "contains" as RuleOp,
  value: "",
  verdict: true,
  note: "",
};

/**
 * Main Cat — build the rules that decide Ecologic / Economic / Illogic, and see what they do before
 * anything acts on them. Read-only towards ExactOnline and Lightspeed: this tab computes and explains.
 */
export function MainCatTab({ isAdmin, onFlash, onError }: { isAdmin: boolean; onFlash: (m: string) => void; onError: (e: unknown) => void }) {
  const utils = trpc.useUtils();
  const rules = trpc.config.mainCat.list.useQuery();
  const [showAttrs, setShowAttrs] = useState(false);
  // Always loaded: the rule editor picks values from it, so it is not an optional extra.
  const attrs = trpc.config.mainCat.attributes.useQuery(undefined, { staleTime: 5 * 60_000 });
  const [previewBrand, setPreviewBrand] = useState("");
  const [previewLabel, setPreviewLabel] = useState<EcoLabel | "">("");
  const [onlyDecided, setOnlyDecided] = useState(true);
  const [page, setPage] = useState(0);
  const preview = trpc.config.mainCat.preview.useQuery(
    { page, pageSize: 100, brand: previewBrand || undefined, label: previewLabel || undefined, onlyDecided },
    { staleTime: 30_000 },
  );
  const [countsOpen, setCountsOpen] = useState(false);
  const counts = trpc.config.mainCat.counts.useQuery(undefined, { enabled: countsOpen, staleTime: 60_000 });

  const [draft, setDraft] = useState(emptyDraft);
  const refresh = () => { utils.config.mainCat.list.invalidate(); utils.config.mainCat.preview.invalidate(); utils.config.mainCat.counts.invalidate(); };
  const upsert = trpc.config.mainCat.upsert.useMutation({ onSuccess: () => { refresh(); setDraft(emptyDraft); onFlash("Regel opgeslagen ✓"); }, onError });
  const del = trpc.config.mainCat.delete.useMutation({ onSuccess: () => { refresh(); onFlash("Regel verwijderd"); }, onError });

  const ruleList = rules.data ?? [];
  const ruleById = new Map(ruleList.map((r) => [r.id, r]));

  const save = () => {
    if (!draft.value.trim()) return;
    upsert.mutate({
      id: draft.id,
      label: draft.label,
      level: draft.level,
      field: draft.level === "article" ? draft.field.trim() : null,
      op: draft.op,
      value: draft.value.trim(),
      verdict: draft.verdict,
      note: draft.note.trim() || null,
    });
  };

  /* ── Value picker ──────────────────────────────────────────────────────────
   * The values are known — the feed has 14 of them under "Ecolabel 1" — so typing them by hand only
   * creates spelling mistakes that match nothing and explain nothing. Picking more than one turns the
   * rule into "is één van", which is one rule instead of four near-identical ones. */
  const selected = draft.op === "anyOf" ? draft.value.split(ANY_OF_SEP).map((v) => v.trim()).filter(Boolean) : draft.value.trim() ? [draft.value.trim()] : [];
  const chosenFields = draft.field.split(ANY_OF_SEP).map((f) => f.trim()).filter(Boolean);
  const toggleField = (f: string) => {
    // The reserved #-fields read the product itself, so they cannot be combined with attribute names.
    if (f.startsWith("#")) { setDraft({ ...draft, field: chosenFields.includes(f) ? "" : f, value: "" }); return; }
    const base = chosenFields.filter((x) => !x.startsWith("#"));
    const next = base.includes(f) ? base.filter((x) => x !== f) : [...base, f];
    setDraft({ ...draft, field: next.join(` ${ANY_OF_SEP} `) });
  };
  /** Values on offer: those of the chosen attributes, or every attribute's values when none is chosen. */
  const pickable = (() => {
    const fields = attrs.data?.fields ?? [];
    const wanted = new Set(chosenFields.filter((f) => !f.startsWith("#")).map((f) => f.toLowerCase()));
    const relevant = wanted.size ? fields.filter((f) => wanted.has(f.field.toLowerCase())) : fields;
    const seen = new Map<string, number>();
    for (const f of relevant) for (const v of f.values) seen.set(v.value, (seen.get(v.value) ?? 0) + v.products);
    return [...seen.entries()].sort((a, b) => b[1] - a[1]).map(([value, products]) => ({ value, products }));
  })();
  const toggleValue = (v: string) => {
    const next = selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v];
    setDraft({
      ...draft,
      value: next.join(` ${ANY_OF_SEP} `),
      // One value reads better as a plain comparison; several can only be "is één van".
      op: next.length > 1 ? "anyOf" : draft.op === "anyOf" ? "equals" : draft.op,
    });
  };

  return (
    <div className="space-y-6">
      <Card title="Hoe Main Cat beslist">
        <div className="space-y-2 text-xs text-slate-600">
          <p>
            Een regel bepaalt of een product <b>wel</b> of <b>niet</b> onder een label valt, en kijkt daarvoor naar één van drie dingen: de
            attributen van het artikel zelf, het merk, of de categorie. De drie labels worden los van elkaar bepaald — een product kan
            tegelijk Ecologic en Economic zijn.
          </p>
          <p>
            De lagen worden afgelopen in de volgorde <b>artikel → merk → categorie</b>, en <b>de eerste laag met een treffer beslist</b>. Zo kan één
            artikel zijn merk tegenspreken en een merk zijn categorie, zonder dat je duizenden productcodes hoeft te onderhouden. Binnen
            dezelfde laag wint een "niet"-regel van een "wel"-regel: een expliciete uitzondering is de scherpere bedoeling, en anders zou de
            uitkomst afhangen van de volgorde waarin je de regels toevallig hebt ingevoerd.
          </p>
          <p>
            Geen enkele regel raakt een product dat door geen enkele regel wordt genoemd: dat blijft <i>onbeslist</i>. "Geen regel zegt er iets
            over" is iets anders dan "een regel zegt nee", en alleen het tweede is een besluit.
          </p>
          <p className="rounded-md bg-amber-50 px-2 py-1.5 text-amber-800">
            Deze tab schrijft <b>niets</b> naar ExactOnline of Lightspeed. De reconcile gebruikt nog de oude, vaste regel (PBS-vlag
            <span className="font-mono"> ecologic</span> + economic-vlag op het merk). Zeg het als de regels hier kloppen, dan zet ik ze aan in
            de sync — dat is een aparte, bewuste stap.
          </p>
        </div>
      </Card>

      <Card
        title={`Regels (${ruleList.length})`}
        actions={<Button variant="secondary" size="sm" onClick={() => setShowAttrs((v) => !v)}>{showAttrs ? "verberg PBS-attributen" : "toon PBS-attributen"}</Button>}
      >
        {showAttrs && (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs text-slate-500">
              Wat de PBS-feed écht levert. Klik een waarde om er een artikelregel van te maken — een regel op een veldnaam die niet bestaat,
              matcht niets en vertelt je ook niet waarom.
            </p>
            {attrs.isLoading && <Spinner label="Attributen inventariseren…" />}
            {attrs.data && !attrs.data.fields.length && <p className="text-xs text-slate-500">Geen attributen gevonden — draait de PBS-detailimport al?</p>}
            <div className="max-h-72 space-y-2 overflow-auto">
              {attrs.data?.fields.map((f) => (
                <div key={f.field} className="text-xs">
                  <div className="font-medium text-slate-700">{f.field} <span className="font-normal text-slate-400">— {f.products.toLocaleString()} artikelen</span></div>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {f.values.map((v) => (
                      <button
                        key={v.value}
                        onClick={() => setDraft({ ...draft, id: undefined, level: "article", field: f.field, op: "contains", value: v.value })}
                        className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600 hover:border-indigo-300 hover:bg-indigo-50"
                        title={`Maak een artikelregel: ${f.field} bevat "${v.value}"`}
                      >
                        {v.value} <span className="text-slate-400">{v.products.toLocaleString()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {attrs.data && <p className="mt-2 text-[11px] text-slate-400">{attrs.data.scanned.toLocaleString()} actieve artikelen doorzocht.</p>}
          </div>
        )}

        {rules.isLoading && <Spinner label="Regels laden…" />}
        {LABELS.map((L) => {
          const forLabel = ruleList.filter((r) => r.label === L.id);
          return (
            <div key={L.id} className="mb-4">
              <div className="mb-1 flex items-center gap-2">
                <Badge color={L.color}>{L.label}</Badge>
                <span className="text-xs text-slate-400">{forLabel.length} regel(s)</span>
              </div>
              {!forLabel.length && <p className="px-1 text-xs text-slate-400">Nog geen regels — dit label wordt voor niemand bepaald.</p>}
              {forLabel.length > 0 && (
                <table className="w-full text-left text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-1">Laag</th><th>Voorwaarde</th><th>Uitkomst</th><th>Notitie</th><th></th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {forLabel.map((r) => (
                      <tr key={r.id} className={r.active ? undefined : "opacity-50"}>
                        <td className="py-1.5 text-xs">{LEVELS.find((l) => l.id === r.level)?.label ?? r.level}</td>
                        <td className="font-mono text-xs">
                          {r.level === "article"
                            ? (r.field?.split(ANY_OF_SEP).map((f) => f.trim()).filter(Boolean).join(" / ") || "elk attribuut")
                            : r.level === "brand" ? "merk" : "categorie"}
                          {" "}{OPS.find((o) => o.id === r.op)?.label}{" "}
                          <span className="text-slate-800">
                            {r.op === "anyOf"
                              ? r.value.split(ANY_OF_SEP).map((v) => v.trim()).filter(Boolean).join(", ")
                              : `"${r.value}"`}
                          </span>
                        </td>
                        <td>{r.verdict ? <Badge color="green">wel</Badge> : <Badge color="red">niet</Badge>}</td>
                        <td className="text-xs text-slate-500">{r.note ?? ""}</td>
                        <td className="text-right">
                          {isAdmin && (
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="secondary" onClick={() => setDraft({ id: r.id, label: r.label, level: r.level, field: r.field ?? "", op: r.op, value: r.value, verdict: r.verdict, note: r.note ?? "" })}>wijzig</Button>
                              <Button size="sm" variant="secondary" onClick={() => upsert.mutate({ id: r.id, label: r.label, level: r.level, field: r.field, op: r.op, value: r.value, verdict: r.verdict, note: r.note, active: !r.active })}>
                                {r.active ? "uit" : "aan"}
                              </Button>
                              <Button size="sm" variant="danger" onClick={() => { if (confirm(`Regel "${r.value}" verwijderen?`)) del.mutate({ id: r.id }); }}>verwijder</Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}

        {isAdmin && (
          <div className="mt-4 rounded-lg border border-slate-200 p-3">
            <div className="mb-2 text-xs font-medium text-slate-600">{draft.id ? `Regel ${draft.id} wijzigen` : "Nieuwe regel"}</div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value as EcoLabel })}>
                {LABELS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </Select>
              <Select value={draft.level} onChange={(e) => setDraft({ ...draft, level: e.target.value as RuleLevel })} title={LEVELS.find((l) => l.id === draft.level)?.hint}>
                {LEVELS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </Select>
              <Select value={draft.op} onChange={(e) => setDraft({ ...draft, op: e.target.value as RuleOp })}>
                {OPS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </Select>
              <Input
                value={draft.value}
                onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                placeholder={draft.level === "brand" ? "merknaam" : draft.level === "category" ? "categorie-id" : "waarde (bijv. FSC)"}
                className="w-56"
                title={draft.op === "anyOf" ? `Meerdere waarden, gescheiden door ${ANY_OF_SEP}` : undefined}
              />
              <Select value={draft.verdict ? "wel" : "niet"} onChange={(e) => setDraft({ ...draft, verdict: e.target.value === "wel" })}>
                <option value="wel">krijgt dit label</option>
                <option value="niet">krijgt dit label NIET</option>
              </Select>
              <Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="notitie (optioneel)" className="w-48" />
              <Button onClick={save} disabled={!draft.value.trim() || upsert.isPending}>{upsert.isPending ? "Opslaan…" : draft.id ? "opslaan" : "toevoegen"}</Button>
              {draft.id && <Button variant="secondary" onClick={() => setDraft(emptyDraft)}>annuleer</Button>}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">{LEVELS.find((l) => l.id === draft.level)?.hint}</p>

            {draft.level === "article" && (
              <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
                <div>
                  <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    In welk attribuut? <span className="font-normal normal-case tracking-normal text-slate-400">niets aangeklikt = alle attributen · meerdere mag</span>
                  </div>
                  {attrs.isLoading && <Spinner label="Attributen laden…" />}
                  <div className="flex flex-wrap gap-1">
                    {(attrs.data?.fields ?? []).map((f) => {
                      const on = chosenFields.includes(f.field);
                      return (
                        <button
                          key={f.field}
                          onClick={() => toggleField(f.field)}
                          className={`rounded-full border px-2 py-0.5 text-[11px] transition ${on ? "border-indigo-400 bg-indigo-50 font-medium text-indigo-700" : "border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:bg-indigo-50"}`}
                        >
                          {f.field} <span className="text-slate-400">{f.products.toLocaleString()}</span>
                        </button>
                      );
                    })}
                    {["#ecologic", "#description", "#brand"].map((f) => {
                      const on = chosenFields.includes(f);
                      return (
                        <button
                          key={f}
                          onClick={() => toggleField(f)}
                          title="Leest het product zelf in plaats van een PBS-attribuut"
                          className={`rounded-full border px-2 py-0.5 font-mono text-[11px] transition ${on ? "border-slate-500 bg-slate-100 font-medium text-slate-800" : "border-dashed border-slate-300 bg-white text-slate-500 hover:border-slate-400"}`}
                        >
                          {f}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    Welke waarde?{" "}
                    <span className="font-normal normal-case tracking-normal text-slate-400">
                      {chosenFields.length ? `waarden uit ${chosenFields.join(" / ")}` : "alle waarden die PBS levert"} · meerdere = "is één van"
                    </span>
                  </div>
                  {!pickable.length && !attrs.isLoading && <p className="text-xs text-slate-400">Geen waarden bekend — draait de PBS-detailimport al?</p>}
                  <div className="flex max-h-40 flex-wrap gap-1 overflow-auto">
                    {pickable.map((v) => {
                      const on = selected.includes(v.value);
                      return (
                        <button
                          key={v.value}
                          onClick={() => toggleValue(v.value)}
                          className={`rounded-full border px-2 py-0.5 text-[11px] transition ${on ? "border-indigo-400 bg-indigo-50 font-medium text-indigo-700" : "border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:bg-indigo-50"}`}
                        >
                          {v.value} <span className="text-slate-400">{v.products.toLocaleString()}</span>
                        </button>
                      );
                    })}
                  </div>
                  {selected.length > 1 && (
                    <p className="mt-1 text-[11px] text-slate-500">{selected.length} waarden geselecteerd — dit wordt één regel "is één van".</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card
        title="Wat de regels opleveren"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input value={previewBrand} onChange={(e) => { setPreviewBrand(e.target.value); setPage(0); }} placeholder="merk…" className="w-40" />
            <Select value={previewLabel} onChange={(e) => { setPreviewLabel(e.target.value as EcoLabel | ""); setPage(0); }}>
              <option value="">alle labels</option>
              {LABELS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </Select>
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={onlyDecided} onChange={(e) => { setOnlyDecided(e.target.checked); setPage(0); }} className="accent-indigo-600" />
              alleen beslist
            </label>
            <Button variant="secondary" size="sm" onClick={() => setCountsOpen((v) => !v)}>{countsOpen ? "verberg totalen" : "tel de hele catalogus"}</Button>
          </div>
        }
      >
        {countsOpen && (
          <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
            {counts.isLoading && <Spinner label="Hele catalogus doorrekenen…" />}
            {counts.data && (
              <div className="space-y-1 text-slate-600">
                <div><b>{counts.data.products.toLocaleString()}</b> actieve artikelen doorgerekend.</div>
                <div className="flex flex-wrap gap-3">
                  {LABELS.map((l) => (
                    <span key={l.id}>
                      <Badge color={l.color}>{l.label}</Badge> {counts.data!.labelled[l.id].toLocaleString()} wel
                      {counts.data!.excludedBy[l.id] > 0 && <span className="text-slate-400"> · {counts.data!.excludedBy[l.id].toLocaleString()} expliciet niet</span>}
                    </span>
                  ))}
                </div>
                <div className="text-slate-500">
                  Beslist door: artikel {counts.data.byLevel.article.toLocaleString()} · merk {counts.data.byLevel.brand.toLocaleString()} · categorie {counts.data.byLevel.category.toLocaleString()}.
                  {" "}<b>{counts.data.undecided.toLocaleString()}</b> artikelen worden door geen enkele regel geraakt.
                </div>
              </div>
            )}
          </div>
        )}

        {preview.isLoading && <Spinner label="Regels toepassen…" />}
        {preview.data && (
          <>
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500"><tr><th className="py-2">Code</th><th>Merk</th><th>Omschrijving</th><th>Labels</th><th>Waarom</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {preview.data.rows.map((r) => (
                  <tr key={r.productCode}>
                    <td className="py-1.5 font-mono text-xs">{r.productCode}</td>
                    <td className="text-xs">{r.brand}</td>
                    <td className="max-w-md truncate text-xs text-slate-600" title={r.description}>{r.description}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {r.labels.length
                          ? r.labels.map((l) => <Badge key={l} color={LABELS.find((x) => x.id === l)?.color ?? "gray"}>{LABELS.find((x) => x.id === l)?.label ?? l}</Badge>)
                          : <span className="text-xs text-slate-300">—</span>}
                      </div>
                    </td>
                    <td className="text-[11px] text-slate-500">
                      {r.decisions.map((d) => (
                        <div key={d.label} title={ruleById.get(d.ruleId)?.note ?? undefined}>
                          <span className="font-medium">{LABELS.find((x) => x.id === d.label)?.label}</span>{d.applies ? "" : " (niet)"}
                          {" ← "}{LEVELS.find((l) => l.id === d.level)?.label.toLowerCase()}: {d.explain}
                        </div>
                      ))}
                      {!r.decisions.length && <span className="text-slate-300">geen regel raakt dit artikel</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!preview.data.rows.length && <p className="py-3 text-xs text-slate-400">Niets te tonen — geen artikel op deze pagina wordt door een regel geraakt.</p>}
            <div className="mt-3 flex items-center gap-2">
              <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>vorige</Button>
              <span className="text-xs text-slate-500">pagina {page + 1}</span>
              <Button variant="secondary" size="sm" disabled={preview.data.rows.length < 100} onClick={() => setPage((p) => p + 1)}>volgende</Button>
              <span className="text-xs text-slate-400">{preview.data.rules} actieve regel(s)</span>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
